// Shape predicates shared across the ProgramReceiptV1 boundary (#4681 E3).
//
// They live in their own portable module so the ONE plain-JSON domain
// predicate serves both sides of the receipt without a second implementation:
// the client-reachable validator in ./program-receipt and the Node-only
// evidence digest in ./program-receipt-evidence-digest (published through
// @helix/lib/server). That split is what keeps the public @helix/lib runtime
// graph free of `node:` builtins — the client-reachable boundary law in
// packages/lib/scripts/client-reachable-boundary.spec.mjs — while leaving the
// receipt vocabulary, types, validator, and mint path reachable from the
// browser and the Edge runtime.
//
// This module is receipt-internal vocabulary: it is deliberately NOT
// re-exported from the contracts barrel or the public @helix/lib barrel.

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

export function isCanonicalIsoTimestamp(value: unknown): value is string {
  if (!isNonEmptyString(value)) return false;
  const parsed = new Date(value);
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString() === value;
}

export function isPlainJsonValue(value: unknown, seen = new Set<object>()): boolean {
  if (value === null) return true;
  if (typeof value === 'string' || typeof value === 'boolean') return true;
  if (typeof value === 'number') {
    // -0 serializes to 0 in JSON: a validated -0 would replay as 0.
    return Number.isFinite(value) && !Object.is(value, -0);
  }
  if (typeof value === 'object') {
    // Cycles would recurse forever here and survive structuredClone — reject.
    if (seen.has(value)) return false;
    seen.add(value);
    try {
      if (Array.isArray(value)) {
        // Sparse arrays and arrays carrying non-index properties: JSON
        // persistence converts holes to null and drops extra props, so the
        // replayed receipt would differ from the validated one. A key count
        // matching the length proves nothing on its own — a negative-integer
        // key balances a hole (`a = new Array(2); a[1] = 1; a['-1'] = 2`) —
        // so every enumerable key must be a canonical, non-negative,
        // in-range index AND every index 0..length-1 must be present.
        let presentIndexCount = 0;
        for (const key of Object.keys(value)) {
          const index = Number(key);
          if (!Number.isInteger(index) || index < 0 || index >= value.length) return false;
          // Canonical decimal spelling only: '01', '1e0', '+1', ' 1', '-0'
          // all parse as in-range integers but are not array indices.
          if (String(index) !== key) return false;
          presentIndexCount += 1;
        }
        // Object.keys never repeats a key, so every counted key is a DISTINCT
        // canonical in-range index; a count equal to length therefore proves
        // every index 0..length-1 is present WITHOUT iterating the length —
        // `new Array(0xffffffff)` must reject in O(present keys), never spin.
        if (presentIndexCount !== value.length) return false;
        return value.every((item) => isPlainJsonValue(item, seen));
      }
      // Realm-agnostic plain-object check (prototype comparison breaks under
      // jest's per-file VM contexts). Map/Date report their own tags; class
      // instances carry a non-Object constructor name on their prototype.
      if (Object.prototype.toString.call(value) !== '[object Object]') return false;
      const proto: unknown = Object.getPrototypeOf(value);
      if (
        proto !== null &&
        (proto as { constructor?: { name?: string } }).constructor?.name !== 'Object'
      ) {
        return false;
      }
      return Object.values(value as Record<string, unknown>).every((item) =>
        isPlainJsonValue(item, seen),
      );
    } finally {
      seen.delete(value);
    }
  }
  return false;
}
